import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import './ManageCustomers.css';

interface Customer {
  uid: string;
  email: string;
  name: string;
  phoneNumber?: string;
  phone?: string;
  role: string;
  status?: 'active' | 'inactive' | 'banned' | 'pending_approval';
  createdAt: any;
  updatedAt?: any;
  profilePhotoUrl?: string;
  avatar?: string;
  totalBookings?: number;
  lastLoginAt?: any;
}

const ManageCustomers: React.FC = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nameFilter, setNameFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [updating, setUpdating] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 50;

  const statusOptions = [
    { value: 'all', label: 'Tất cả' },
    { value: 'active', label: 'Hoạt động' },
    { value: 'inactive', label: 'Tạm dừng' },
    { value: 'banned', label: 'Bị cấm' },
    { value: 'pending_approval', label: 'Chờ phê duyệt' }
  ];

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Bắt đầu tải danh sách khách hàng...');

      let customersQuery;
      let querySnapshot;

      try {
        // Xây dựng query theo status để giảm dữ liệu tải về
        const constraints: any[] = [where('role', '==', 'customer')];
        if (statusFilter !== 'all') {
          constraints.push(where('status', '==', statusFilter));
        }
        try {
          customersQuery = query(
            collection(db, 'users'),
            ...constraints,
            orderBy('createdAt', 'desc')
          );
          console.log('Thử query với orderBy...');
          querySnapshot = await getDocs(customersQuery);
        } catch (indexError) {
          console.warn('Query với orderBy thất bại, thử query không order:', indexError);
          customersQuery = query(
            collection(db, 'users'),
            ...constraints
          );
          console.log('Thử query đơn giản...');
          querySnapshot = await getDocs(customersQuery);
        }
      } catch (qErr) {
        console.warn('Xây dựng query có điều kiện thất bại, fallback lấy tất cả customer:', qErr);
        customersQuery = query(
          collection(db, 'users'),
          where('role', '==', 'customer')
        );
        querySnapshot = await getDocs(customersQuery);
      }

      const customersData: Customer[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        console.log('Customer doc:', doc.id, data);
        
        customersData.push({
          uid: doc.id,
          email: data.email || '',
          name: data.name || data.displayName || '',
          phoneNumber: data.phoneNumber,
          phone: data.phone,
          role: data.role,
          status: data.status || 'active',
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          profilePhotoUrl: data.profilePhotoUrl,
          avatar: data.avatar,
          totalBookings: data.totalBookings || 0,
          lastLoginAt: data.lastLoginAt
        });
      });

      // Sắp xếp thủ công nếu cần
      if (customersData.length > 0) {
        customersData.sort((a, b) => {
          if (!a.createdAt && !b.createdAt) return 0;
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          
          const aTime = a.createdAt?.seconds ? a.createdAt.seconds : 0;
          const bTime = b.createdAt?.seconds ? b.createdAt.seconds : 0;
          return bTime - aTime;
        });
      }

      console.log(`Đã tải ${customersData.length} khách hàng`);
      setCustomers(customersData);
      setFilteredCustomers(customersData);
      
    } catch (error: any) {
      console.error('Lỗi chi tiết khi tải danh sách khách hàng:', error);
      
      let errorMessage = 'Không thể tải danh sách khách hàng';
      
      if (error.code === 'permission-denied') {
        errorMessage = 'Không có quyền truy cập dữ liệu. Vui lòng kiểm tra quyền đăng nhập.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Dịch vụ hiện không khả dụng. Vui lòng thử lại sau.';
      } else if (error.code === 'failed-precondition') {
        errorMessage = 'Cần tạo index cho database. Vui lòng liên hệ quản trị viên.';
      } else if (error.message) {
        errorMessage = `Lỗi: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const normalizePhoneDigits = (value: string) => value.replace(/\D/g, '');

  const applyFilters = (customersToFilter: Customer[]) => {
    let filtered = customersToFilter;

    // Filter theo tên, email hoặc số điện thoại
    const q = nameFilter.trim();
    if (q) {
      const qLower = q.toLowerCase();
      const qDigits = normalizePhoneDigits(q);
      filtered = filtered.filter((customer) => {
        if (customer.name.toLowerCase().includes(qLower)) return true;
        if (customer.email.toLowerCase().includes(qLower)) return true;
        const p1 = String(customer.phoneNumber ?? '');
        const p2 = String(customer.phone ?? '');
        const phoneCombined = `${p1} ${p2}`.trim().toLowerCase();
        if (phoneCombined && phoneCombined.includes(qLower)) return true;
        if (qDigits.length > 0) {
          const phoneDigits = normalizePhoneDigits(p1 + p2);
          if (phoneDigits.includes(qDigits)) return true;
        }
        return false;
      });
    }

    // Filter theo trạng thái
    if (statusFilter !== 'all') {
      filtered = filtered.filter(customer => customer.status === statusFilter);
    }

    return filtered;
  };

  const updateCustomerStatus = async (customerId: string, newStatus: string) => {
    try {
      setUpdating(customerId);
      const customerRef = doc(db, 'users', customerId);
      await updateDoc(customerRef, {
        status: newStatus,
        updatedAt: new Date()
      });

      // Cập nhật state local
      const updatedCustomers = customers.map(customer =>
        customer.uid === customerId
          ? { ...customer, status: newStatus as any }
          : customer
      );
      setCustomers(updatedCustomers);

      // Áp dụng lại filter
      const filtered = applyFilters(updatedCustomers);
      setFilteredCustomers(filtered);

    } catch (error) {
      console.error('Lỗi khi cập nhật trạng thái:', error);
      setError('Cập nhật trạng thái thất bại');
    } finally {
      setUpdating(null);
    }
  };

  const getStatusLabel = (status?: string) => {
    const statusMap: { [key: string]: string } = {
      'active': 'Hoạt động',
      'inactive': 'Tạm dừng',
      'banned': 'Bị cấm',
      'pending_approval': 'Chờ phê duyệt'
    };
    return status ? statusMap[status] || status : 'Hoạt động';
  };

  const getStatusClass = (status?: string) => {
    const statusClassMap: { [key: string]: string } = {
      'active': 'status-active',
      'inactive': 'status-inactive',
      'banned': 'status-banned',
      'pending_approval': 'status-pending'
    };
    return status ? statusClassMap[status] || '' : 'status-active';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Chưa cập nhật';
    
    try {
      let date: Date;
      
      if (timestamp.seconds) {
        // Firestore Timestamp
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp.toDate) {
        // Firestore Timestamp object
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        // JavaScript Date
        date = timestamp;
      } else {
        // String or number
        date = new Date(timestamp);
      }
      
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Lỗi định dạng';
    }
  };

  const getPhoneNumber = (customer: Customer) => {
    return customer.phoneNumber || customer.phone || 'Chưa cập nhật';
  };

  // Effect: fetch khi vào trang và khi statusFilter đổi (server-side filter)
  useEffect(() => {
    setCurrentPage(1);
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Memoized filtered customers (client-side tên / email / SĐT + trạng thái)
  const memoFilteredCustomers = useMemo(() => applyFilters(customers), [customers, nameFilter, statusFilter]);

  // Pagination derived state
  const totalPages = useMemo(() => Math.ceil(memoFilteredCustomers.length / recordsPerPage) || 1, [memoFilteredCustomers.length]);
  const displayedCustomers = useMemo(() => {
    const start = (currentPage - 1) * recordsPerPage;
    return memoFilteredCustomers.slice(start, start + recordsPerPage);
  }, [memoFilteredCustomers, currentPage]);

  useEffect(() => {
    setFilteredCustomers(memoFilteredCustomers);
    if (currentPage > totalPages) setCurrentPage(1);
  }, [memoFilteredCustomers, currentPage, totalPages]);

  if (loading) {
    return (
      <div className="manage-customers">
        <div className="page-header">
          <h2>Quản lý khách hàng</h2>
          <p className="page-subtitle">Quản lý và theo dõi tất cả khách hàng trong hệ thống</p>
        </div>
        <div className="dashboard-loading">
          <div className="loading-spinner">Đang tải dữ liệu...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-customers">
      <div className="page-header">
        <h2>Quản lý khách hàng</h2>
        <p className="page-subtitle">Quản lý và theo dõi tất cả khách hàng trong hệ thống</p>
      </div>

      <div className="filters">
        <div className="filter-group">
          <label htmlFor="name-filter">Tìm kiếm:</label>
          <input
            id="name-filter"
            type="text"
            placeholder="Tên, email hoặc số điện thoại..."
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label htmlFor="status-filter">Trạng thái:</label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <button onClick={fetchCustomers} className="refresh-button">
          Làm mới
        </button>
      </div>

      {error && (
        <div className="error-message">
          <div>
            <strong>{error}</strong>
            <br />
            <small>💡 Mở Developer Console (F12) để xem thông tin chi tiết về lỗi</small>
          </div>
          <button 
            onClick={() => {
              setError(null);
              fetchCustomers();
            }}
            className="retry-button"
          >
            Thử lại
          </button>
        </div>
      )}

      <div className="table-container">
        <table className="customers-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Tên</th>
              <th>Số điện thoại</th>
              <th>Email</th>
              <th>Trạng thái</th>
              <th>Tổng booking</th>
              <th>Ngày tạo</th>
            </tr>
          </thead>
          <tbody>
            {displayedCustomers.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">
                  {memoFilteredCustomers.length === 0 ? 'Không có khách hàng nào' : 'Không có dữ liệu cho trang này'}
                </td>
              </tr>
            ) : (
              displayedCustomers.map((customer, index) => (
                <tr key={customer.uid}>
                  <td className="order-cell">
                    <span className="order-display">{(currentPage - 1) * recordsPerPage + index + 1}</span>
                  </td>
                  <td>
                    <button 
                      className="customer-name-link"
                      onClick={() => navigate(`/admin/customers/${customer.uid}`)}
                      title="Xem chi tiết khách hàng"
                    >
                      {customer.name}
                    </button>
                  </td>
                  <td>{getPhoneNumber(customer)}</td>
                  <td className="email-cell" title={customer.email}>{customer.email}</td>
                  <td>
                    <span className={`status ${getStatusClass(customer.status)}`}>
                      {getStatusLabel(customer.status)}
                    </span>
                  </td>
                  <td className="booking-count-cell">{customer.totalBookings || 0}</td>
                  <td className="date-cell">{formatDate(customer.createdAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <div className="pagination-info">
            Hiển thị {(currentPage - 1) * recordsPerPage + 1} - {Math.min(currentPage * recordsPerPage, memoFilteredCustomers.length)} của {memoFilteredCustomers.length} kết quả
          </div>
          <div className="pagination-controls">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="pagination-btn">««</button>
            <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="pagination-btn">«</button>
            {(() => {
              const buttons = [] as React.ReactElement[];
              const start = Math.max(1, currentPage - 2);
              const end = Math.min(totalPages, currentPage + 2);
              for (let i = start; i <= end; i++) {
                buttons.push(
                  <button key={i} onClick={() => setCurrentPage(i)} disabled={i === currentPage} className={`pagination-btn ${i === currentPage ? 'active' : ''}`}>{i}</button>
                );
              }
              return buttons;
            })()}
            <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="pagination-btn">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="pagination-btn">»»</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageCustomers; 